import { tx } from "../lib/i18n";
import { useEffect, useState, useCallback } from "react";
import { BarChart3, UserCheck, BookOpen, CalendarCheck, CreditCard, Video, Wallet, ShieldAlert, Banknote, Quote, Settings, Mail, Download, Check, X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "../lib/api";
import { fmtFull, fmtDate, money, monthLabel } from "../lib/format";
import { DashShell, Stat, Badge, Table, Section, useTab } from "../components/dash/DashKit";
import { Spinner } from "../components/site/Layout";

const useLoad = (path, params) => {
  const [d, setD] = useState(null);
  const key = JSON.stringify(params || {});
  const load = useCallback(() => api.get(path, { params }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [path, key]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  return [d, load];
};

const DOC_LABEL = { id: "ID", diploma: "Diploma", good_conduct: "VOG" };

function Overview() {
  const [d] = useLoad("/admin/overview");
  if (!d) return <Spinner />;
  return (
    <>
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label={tx("Aanmeldingen open")} value={d.pending_teachers} icon={UserCheck} testid="admin-stat-pending" />
        <Stat label={tx("Actieve lessen")} value={d.active_lessons} icon={BookOpen} testid="admin-stat-lessons" />
        <Stat label={tx("Betaalde boekingen")} value={d.paid_bookings} icon={CalendarCheck} testid="admin-stat-bookings" />
        <Stat label={tx("Ouders")} value={d.parents} icon={UserCheck} testid="admin-stat-parents" />
        <Stat label={tx("Open alerts")} value={d.open_alerts} icon={ShieldAlert} testid="admin-stat-alerts" />
      </div>
      <Section title={tx("Maandelijkse omzet")}>
        <Table testid="revenue-table" rows={d.revenue} cols={[
          { label: tx("Maand"), render: (r) => monthLabel(r.month) },
          { label: tx("Betalingen"), key: "payments" },
          { label: tx("Ontvangen (Sentoo)"), render: (r) => money(r.paid) },
          { label: tx("Lestegoed gebruikt"), render: (r) => money(r.credit_used) },
          { label: tx("Commissie (gegeven lessen)"), render: (r) => money(r.commission || 0) },
          { label: tx("Netto docenten"), render: (r) => money(r.teacher_net || 0) },
        ]} />
      </Section>
    </>
  );
}

function Applications() {
  const [status, setStatus] = useState("pending");
  const [rows, load] = useLoad("/admin/teachers", status ? { status } : {});
  const act = async (id, s) => { await api.post(`/admin/teachers/${id}/status`, { status: s }); toast.success(tx("Status bijgewerkt")); load(); };
  return (
    <Section title={tx("Docentaanmeldingen")} action={
      <select className="osk-input max-w-[200px]" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="applications-status-filter">
        <option value="pending">{tx("In behandeling")}</option><option value="approved">{tx("Goedgekeurd")}</option><option value="rejected">{tx("Afgewezen")}</option><option value="">{tx("Alle")}</option>
      </select>}>
      {!rows ? <Spinner /> : <div className="space-y-3">
        {rows.map((t) => (
          <div key={t.user_id} className="osk-card p-5" data-testid={`application-${t.user_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-lg font-extrabold">{t.display_name} <span className="text-sm font-normal text-osk-muted">{t.email}</span></p>
                <p className="text-sm text-osk-text">{t.subjects.join(", ")} · {t.school_types.join(", ")} · {t.languages.join(", ")}</p>
                <p className="mt-1 max-w-2xl text-sm text-osk-text">{t.bio}</p>
                <p className="mt-1 text-xs text-osk-muted">Bank: {t.bank?.name} {t.bank?.account} ({t.bank?.holder}) · {tx("Voorwaarden")} v{t.terms_version} {tx("op")} {fmtDate(t.terms_accepted_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge status={t.status} />
                {t.status !== "approved" && <button className="osk-btn-primary px-4 py-1.5 text-xs" onClick={() => act(t.user_id, "approved")} data-testid={`approve-${t.user_id}`}><Check className="h-4 w-4" />{tx("Goedkeuren")}</button>}
                {t.status !== "rejected" && <button className="osk-btn border border-osk-red px-4 py-1.5 text-xs text-osk-red" onClick={() => act(t.user_id, "rejected")} data-testid={`reject-${t.user_id}`}><X className="h-4 w-4" />{tx("Afwijzen")}</button>}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {t.documents.map((d) => <button key={d.id} onClick={() => downloadFile(`/files/${d.id}`, d.filename)} className="osk-pill bg-osk-light text-osk-blue" data-testid={`doc-${d.id}`}><Download className="mr-1 h-3.5 w-3.5" />{DOC_LABEL[d.doc_type]}: {d.filename}</button>)}
              {!t.documents.length && <span className="text-xs text-osk-red">{tx("Nog geen documenten geüpload")}</span>}
            </div>
          </div>
        ))}
        {!rows.length && <div className="osk-card p-8 text-center text-osk-muted">{tx("Geen aanmeldingen.")}</div>}
      </div>}
    </Section>
  );
}

function Lessons() {
  const [rows, load] = useLoad("/admin/lessons");
  const toggle = async (l) => { await api.post(`/admin/lessons/${l.id}/status`, { status: l.status === "active" ? "archived" : "active" }); load(); };
  if (!rows) return <Spinner />;
  return <Table testid="admin-lessons-table" rows={rows} cols={[
    { label: tx("Les"), render: (r) => <b>{r.title}</b> }, { label: tx("Docent"), key: "teacher_name" },
    { label: tx("Niveau"), render: (r) => `${r.school_type} ${r.grade}` }, { label: tx("Vorm"), render: (r) => tx(r.format) },
    { label: tx("Rooster"), render: (r) => tx({ single: "Losse les", weekly: "Wekelijkse reeks", block: "Cursusblok" }[r.schedule_type]) }, { label: tx("Prijs"), render: (r) => money(r.price, r.currency) }, { label: tx("Eiland"), render: (r) => ({ aruba: "Aruba", curacao: "Curaçao", bonaire: "Bonaire", sint_maarten: "Sint Maarten" }[r.island] || r.island) },
    { label: tx("Status"), render: (r) => <Badge status={r.status} /> },
    { label: "", render: (r) => <button className="text-xs font-bold text-osk-blue" onClick={() => toggle(r)} data-testid={`admin-toggle-lesson-${r.id}`}>{r.status === "active" ? tx("Archiveren") : tx("Activeren")}</button> },
  ]} />;
}

function Bookings() {
  const [rows] = useLoad("/admin/bookings");
  if (!rows) return <Spinner />;
  return <Table testid="admin-bookings-table" rows={rows} cols={[
    { label: tx("Aangemaakt"), render: (r) => fmtDate(r.created_at) }, { label: tx("Ouder"), key: "parent_name" }, { label: tx("Kind"), key: "child_name" },
    { label: tx("Les"), key: "lesson_title" }, { label: tx("Type"), render: (r) => r.month ? monthLabel(r.month) : tx({ single: "Losse les", block: "Cursusblok", weekly_month: "Wekelijkse reeks" }[r.type] || r.type) },
    { label: tx("Bedrag"), render: (r) => money(r.amount, r.currency) }, { label: tx("Status"), render: (r) => <Badge status={r.status} /> },
  ]} />;
}

function Payments() {
  const [rows] = useLoad("/admin/payments");
  if (!rows) return <Spinner />;
  return <Table testid="admin-payments-table" rows={rows} cols={[
    { label: tx("Datum"), render: (r) => fmtDate(r.created_at) }, { label: tx("Ouder"), key: "parent_name" }, { label: tx("Bon"), render: (r) => r.receipt_number || "-" },
    { label: tx("Provider"), render: (r) => r.provider === "test" ? "Sentoo (TEST)" : r.provider === "credit" ? tx("Lestegoed") : r.provider }, { label: tx("Bedrag"), render: (r) => money(r.amount, r.currency) },
    { label: tx("Lestegoed"), render: (r) => money(r.credit_applied, r.currency) }, { label: tx("Status"), render: (r) => <Badge status={r.status} /> },
  ]} />;
}

function Sessions() {
  const [scope, setScope] = useState("upcoming");
  const [rows, load] = useLoad("/admin/sessions", { scope });
  const cancel = async (s) => {
    if (!window.confirm(tx("Sessie annuleren? Ingeschreven ouders ontvangen lestegoed."))) return;
    try { await api.post(`/admin/sessions/${s.id}/cancel`); toast.success(tx("Geannuleerd")); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title={tx("Sessies")} action={<select className="osk-input max-w-[180px]" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="admin-sessions-scope"><option value="upcoming">{tx("Komend")}</option><option value="past">{tx("Afgelopen")}</option></select>}>
      {!rows ? <Spinner /> : <Table testid="admin-sessions-table" rows={rows} cols={[
        { label: tx("Start"), render: (r) => fmtFull(r.start) }, { label: tx("Les"), key: "lesson_title" }, { label: tx("Docent"), key: "teacher_name" },
        { label: tx("Bezet"), key: "seats_taken" }, { label: tx("Aanwezig"), render: (r) => r.attendance.map((a) => `${a.name} (${Math.round(a.minutes)}m)`).join(", ") || "-" },
        { label: tx("Status"), render: (r) => <Badge status={r.status} session /> },
        { label: "", render: (r) => r.status === "scheduled" && <button className="text-xs font-bold text-osk-red" onClick={() => cancel(r)} data-testid={`admin-cancel-session-${r.id}`}>{tx("Annuleren")}</button> },
      ]} />}
    </Section>
  );
}

function Credits() {
  const [rows, load] = useLoad("/admin/credits");
  const [parents] = useLoad("/admin/parents");
  const [f, setF] = useState({ parent_id: "", amount: "", reason: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/credits", { ...f, amount: Number(f.amount), currency: "AWG" }); toast.success(tx("Lestegoed bijgewerkt")); setF({ parent_id: "", amount: "", reason: "" }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <>
      <form onSubmit={submit} className="osk-card mb-6 grid gap-3 p-5 md:grid-cols-[1fr_140px_1fr_auto]" data-testid="admin-credit-form">
        <select className="osk-input" required value={f.parent_id} onChange={(e) => setF({ ...f, parent_id: e.target.value })} data-testid="admin-credit-parent-select">
          <option value="">{tx("Kies ouder")}</option>
          {(parents || []).map((p) => <option key={p.id} value={p.id}>{p.name} – {p.email} ({tx("saldo")} {money(p.credits.AWG || 0)})</option>)}
        </select>
        <input className="osk-input" type="number" step="0.01" required placeholder="+/- AWG" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="admin-credit-amount-input" />
        <input className="osk-input" required placeholder={tx("Reden")} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="admin-credit-reason-input" />
        <button className="osk-btn-primary py-2" data-testid="admin-credit-submit-btn">{tx("Opslaan")}</button>
      </form>
      {!rows ? <Spinner /> : <Table testid="admin-credits-table" rows={rows} cols={[
        { label: tx("Datum"), render: (r) => fmtDate(r.created_at) }, { label: tx("Ouder"), key: "parent_email" }, { label: tx("Reden"), render: (r) => tx(r.reason) },
        { label: tx("Bedrag"), render: (r) => <b className={r.amount > 0 ? "text-emerald-700" : "text-osk-red"}>{money(r.amount, r.currency)}</b> },
      ]} />}
    </>
  );
}

function Alerts() {
  const [rows, load] = useLoad("/admin/alerts");
  const check = async () => { const { data } = await api.post("/admin/alerts/check"); toast.success(tx("{n} nieuwe alert(s)", { n: data.created })); load(); };
  const resolve = async (id) => { await api.post(`/admin/alerts/${id}/resolve`); load(); };
  return (
    <Section title={tx("Bypass-alerts")} action={<button className="osk-btn-outline py-2 text-sm" onClick={check} data-testid="alerts-check-btn">{tx("Nu controleren")}</button>}>
      <p className="mb-3 text-sm text-osk-text">{tx("Melding wanneer een kind dat 2 of meer opeenvolgende maanden bij een docent boekte, stopt met boeken bij die docent.")}</p>
      {!rows ? <Spinner /> : <Table testid="alerts-table" rows={rows} empty={tx("Geen alerts.")} cols={[
        { label: tx("Maand"), key: "month" }, { label: tx("Docent"), key: "teacher_name" }, { label: tx("Kind"), key: "child_name" }, { label: tx("Ouder"), key: "parent_email" },
        { label: tx("Geboekt in"), render: (r) => r.months_booked.join(", ") },
        { label: "", render: (r) => r.resolved ? <span className="text-xs text-osk-muted">{tx("Afgehandeld")}</span> : <button className="text-xs font-bold text-osk-blue" onClick={() => resolve(r.id)} data-testid={`resolve-alert-${r.id}`}>{tx("Afhandelen")}</button> },
      ]} />}
    </Section>
  );
}

function Payouts() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rows, load] = useLoad("/admin/payouts", { month });
  const mark = async (r) => { await api.post("/admin/payouts/mark", { teacher_id: r.teacher_id, month, paid: !r.paid }); load(); };
  return (
    <Section title={tx("Uitbetalingen per docent")} action={
      <div className="flex items-center gap-2">
        <input type="month" className="osk-input" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payouts-month-input" />
        <button className="osk-btn-outline whitespace-nowrap py-2 text-sm" onClick={() => downloadFile(`/admin/payouts/csv?month=${month}`, `uitbetalingen-${month}.csv`)} data-testid="payouts-csv-btn"><Download className="h-4 w-4" />CSV</button>
      </div>}>
      {!rows ? <Spinner /> : <Table testid="payouts-table" rows={rows} cols={[
        { label: tx("Docent"), key: "teacher_name" }, { label: tx("Lessen"), key: "sessions" }, { label: tx("Bruto"), render: (r) => money(r.gross) },
        { label: tx("Commissie"), render: (r) => money(r.commission) }, { label: tx("Netto"), render: (r) => <b>{money(r.net)}</b> },
        { label: tx("Bank"), render: (r) => <span className="text-xs">{r.bank.name}<br />{r.bank.account}<br />{r.bank.holder}</span> },
        { label: tx("Status"), render: (r) => <button onClick={() => mark(r)} data-testid={`payout-mark-${r.teacher_id}`} className={`osk-pill ${r.paid ? "bg-tile-mint text-emerald-700" : "bg-tile-yellow text-amber-700"}`}>{r.paid ? tx("Uitbetaald") : tx("Markeer als betaald")}</button> },
      ]} />}
    </Section>
  );
}

function Testimonials() {
  const [rows, load] = useLoad("/admin/testimonials");
  const [f, setF] = useState({ quote: "", author: "", role_text: "" });
  const add = async (e) => { e.preventDefault(); await api.post("/admin/testimonials", { ...f, order: (rows || []).length }); setF({ quote: "", author: "", role_text: "" }); load(); };
  const save = async (t) => { await api.put(`/admin/testimonials/${t.id}`, t); toast.success(tx("Opgeslagen")); load(); };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); load(); };
  const photo = async (id, file) => { const fd = new FormData(); fd.append("file", file); try { await api.post(`/admin/testimonials/${id}/photo`, fd); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!rows) return <Spinner />;
  return (
    <div className="space-y-4">
      {rows.map((t) => <TestimonialRow key={t.id} t={t} onSave={save} onDelete={del} onPhoto={photo} />)}
      <form onSubmit={add} className="osk-card space-y-3 p-5" data-testid="testimonial-add-form">
        <h3 className="text-lg">{tx("Nieuwe testimonial")}</h3>
        <textarea className="osk-input" required placeholder={tx("Quote")} value={f.quote} onChange={(e) => setF({ ...f, quote: e.target.value })} data-testid="testimonial-quote-input" />
        <div className="grid gap-3 sm:grid-cols-2">
          <input className="osk-input" required placeholder={tx("Naam (bijv. Mw. Reyes)")} value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} data-testid="testimonial-author-input" />
          <input className="osk-input" placeholder={tx("Rol (bijv. ouder van een HAVO-leerling)")} value={f.role_text} onChange={(e) => setF({ ...f, role_text: e.target.value })} data-testid="testimonial-role-input" />
        </div>
        <button className="osk-btn-primary py-2" data-testid="testimonial-add-btn"><Plus className="h-4 w-4" />{tx("Toevoegen")}</button>
      </form>
    </div>
  );
}

const TestimonialRow = ({ t, onSave, onDelete, onPhoto }) => {
  const [f, setF] = useState(t);
  return (
    <div className="osk-card space-y-3 p-5" data-testid={`testimonial-row-${t.id}`}>
      <textarea className="osk-input" value={f.quote} onChange={(e) => setF({ ...f, quote: e.target.value })} />
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_90px_auto]">
        <input className="osk-input" value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} />
        <input className="osk-input" value={f.role_text} onChange={(e) => setF({ ...f, role_text: e.target.value })} />
        <input className="osk-input" type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />{tx("Actief")}</label>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button className="osk-btn-primary px-4 py-1.5 text-sm" onClick={() => onSave(f)} data-testid={`testimonial-save-${t.id}`}>{tx("Opslaan")}</button>
        <label className="cursor-pointer text-sm font-bold text-osk-blue">{tx("Foto uploaden")}<input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onPhoto(t.id, e.target.files[0])} /></label>
        <button className="text-osk-muted hover:text-osk-red" onClick={() => onDelete(t.id)} data-testid={`testimonial-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

function SettingsTab() {
  const [s, load] = useLoad("/admin/settings");
  const [f, setF] = useState(null);
  useEffect(() => { if (s) setF({ commission_rate: s.commission_rate, loyalty_rate: s.loyalty_rate, loyalty_months: s.loyalty_months, cancel_window_hours: s.cancel_window_hours, format_max: s.format_max }); }, [s]);
  if (!s || !f) return <Spinner />;
  const save = async () => {
    try { await api.put("/admin/settings", { ...f, commission_rate: Number(f.commission_rate), loyalty_rate: Number(f.loyalty_rate), loyalty_months: Number(f.loyalty_months), cancel_window_hours: Number(f.cancel_window_hours) }); toast.success(tx("Instellingen opgeslagen")); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const island = async (code, enabled) => { await api.put(`/admin/islands/${code}`, { enabled }); load(); };
  const Num = ({ k, label }) => <label className="block"><span className="osk-label">{label}</span><input type="number" className="osk-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`setting-${k}`} /></label>;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="osk-card space-y-4 p-6" data-testid="settings-form">
        <h3 className="text-lg">{tx("Commissie & annulering")}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {Num({ k: "commission_rate", label: tx("Platformcommissie (%)") })}
          {Num({ k: "loyalty_rate", label: tx("Loyaliteitscommissie (%)") })}
          {Num({ k: "loyalty_months", label: tx("Loyaliteit na x opeenvolgende maanden") })}
          {Num({ k: "cancel_window_hours", label: tx("Annuleringstermijn (uren)") })}
        </div>
        <h3 className="pt-2 text-lg">{tx("Max. kinderen per lesvorm")}</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(f.format_max).map(([k, v]) => (
            <label key={k} className="block"><span className="osk-label">{tx(k)}</span>
              <input type="number" min="1" className="osk-input" value={v} onChange={(e) => setF({ ...f, format_max: { ...f.format_max, [k]: Number(e.target.value) } })} data-testid={`setting-format-${k}`} /></label>
          ))}
        </div>
        <button className="osk-btn-primary" onClick={save} data-testid="settings-save-btn">{tx("Opslaan")}</button>
      </div>
      <div className="osk-card space-y-3 p-6" data-testid="islands-settings">
        <h3 className="text-lg">{tx("Eilanden")}</h3>
        {s.islands.map((i) => (
          <label key={i.code} className="flex items-center justify-between rounded-xl bg-[#F7FAFE] p-3">
            <span className="font-bold">{i.name} <span className="text-sm font-normal text-osk-muted">({i.currency})</span></span>
            <input type="checkbox" className="h-5 w-5 accent-[#1565D8]" checked={i.enabled} onChange={(e) => island(i.code, e.target.checked)} data-testid={`island-toggle-${i.code}`} />
          </label>
        ))}
      </div>
    </div>
  );
}

function Emails() {
  const [rows] = useLoad("/admin/emails");
  if (!rows) return <Spinner />;
  return <Table testid="emails-table" rows={rows} cols={[
    { label: tx("Tijd"), render: (r) => fmtFull(r.created_at) }, { label: tx("Aan"), key: "to" }, { label: tx("Type"), key: "kind" },
    { label: tx("Onderwerp"), key: "subject" }, { label: tx("Status"), render: (r) => <span className="osk-pill bg-slate-100">{r.status}</span> },
    { label: tx("Link"), render: (r) => r.link ? <a href={r.link} className="text-xs font-bold text-osk-blue" target="_blank" rel="noreferrer">{tx("open")}</a> : "-" },
  ]} />;
}

const TABS = [["overview", "Overzicht", BarChart3], ["applications", "Aanmeldingen", UserCheck], ["lessons", "Lessen", BookOpen], ["bookings", "Boekingen", CalendarCheck],
  ["payments", "Betalingen", CreditCard], ["sessions", "Sessies", Video], ["credits", "Lestegoed", Wallet], ["alerts", "Bypass-alerts", ShieldAlert],
  ["payouts", "Uitbetalingen", Banknote], ["testimonials", "Testimonials", Quote], ["settings", "Instellingen", Settings], ["emails", "E-maillog", Mail]];
const VIEWS = { overview: Overview, applications: Applications, lessons: Lessons, bookings: Bookings, payments: Payments, sessions: Sessions, credits: Credits,
  alerts: Alerts, payouts: Payouts, testimonials: Testimonials, settings: SettingsTab, emails: Emails };

export default function AdminDashboard() {
  const [tab, setTab] = useTab("overview");
  const View = VIEWS[tab] || Overview;
  return <DashShell testid="admin-dashboard" title="Admin" sub="Beheer van Onskola" tabs={TABS} tab={tab} setTab={setTab}><View /></DashShell>;
}
